"use client";

import React, {
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UserSignupEvents, trackEvent } from "@/analytics";
import { addUserPostalCode, getIpData, getIsEuCountry } from "@/data/zipcode";
import { usePathname, useSearchParams } from "next/navigation";

import AddPhoneModal from "@/components/add-phone-modal";
import { Database } from "@/data/supabase.types";
import Loader from "./icons/Loader";
import Modal from "@/components/common/Modal";
import PostalCodeFields from "./PostalCodeFields";
import { PunchUpLogo } from "@/components/Logo";
import { createClientComponentClient } from "@/utils/supabase/client";
import dayjs from "dayjs";

export default function WelcomeModal() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isMounted, setIsMounted] = useState(false);
  const showWelcomeBanner = searchParams?.get("showWelcomeBanner");
  const forceZipModal = searchParams?.get("showZipModal") === "true";
  const forcePhoneModal = searchParams?.get("showPhoneModal") === "true";
  const supabase = createClientComponentClient<Database>({});

  const [showModal, setShowModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [postalCode, setPostalCode] = useState("");
  const [error, setError] = useState("");
  const [detectedCountryCode, setDetectedCountryCode] = useState<string | null>(
    null
  );
  const [enteredCountryCode, setEnteredCountryCode] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  // Handle mounting state
  useEffect(() => {
    setIsMounted(true);
  }, [
    showWelcomeBanner,
    pathname,
    searchParams,
    forceZipModal,
    forcePhoneModal,
  ]);

  const isUS = useMemo(() => {
    if (enteredCountryCode) {
      return enteredCountryCode === "US";
    }
    return detectedCountryCode === "US";
  }, [detectedCountryCode, enteredCountryCode]);

  const shouldShowPhoneModal = useCallback(
    async (user: any) => {
      if (!user) return false;

      const { data: userExtra } = await supabase
        .from("users_extra")
        .select("phone, last_active_at")
        .eq("id", user.id)
        .single();

      if (!userExtra) return false;
      if (userExtra.phone) return false;

      const oneWeekAgo = dayjs().subtract(7, "days");
      const lastActive = userExtra.last_active_at
        ? dayjs(userExtra.last_active_at)
        : null;

      return (lastActive && lastActive.isBefore(oneWeekAgo)) || forcePhoneModal;
    },
    [supabase, forcePhoneModal]
  );

  const isValidPostalCode = (postalCode: string) => {
    if (isUS) {
      return /^[0-9]{5}(?:-[0-9]{4})?$/.test(postalCode);
    }
    return /^[a-zA-Z0-9-\s]{0,10}$/.test(postalCode);
  };
  useEffect(() => {
    const fetchUser = async () => {
      if (hasInitialized) return;
      setHasInitialized(true);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;

        if (!user) return;

        const { data, error } = await supabase
          .from("user_zipcodes")
          .select("*")
          .eq("user", user?.id);

        if (error) {
          console.error("Error getting user zipcodes", error);
          return;
        }

        const firstItem = data && data[0];
        const shouldShowZip =
          forceZipModal ||
          !firstItem ||
          (firstItem &&
            (!firstItem.zipcode || firstItem.zipcode.trim() === "")) ||
          (firstItem &&
            (firstItem.country_code === null || firstItem.is_eu === null));

        if (shouldShowZip) {
          // If we need to show zip modal, don't show phone modal
          setShowPhoneModal(false);

          if (
            !firstItem ||
            (firstItem &&
              (!firstItem.zipcode || firstItem.zipcode.trim() === ""))
          ) {
            try {
              const ipData = await getIpData();

              const {
                postalCode: postalCodeFromIP,
                countryCode,
                isEu,
              } = ipData;

              if (countryCode && !forceZipModal) {
                try {
                  await addUserPostalCode({
                    supabase,
                    postalCode: postalCodeFromIP || "00000",
                    countryCode,
                    isFromIP: true,
                    isEu: isEu ?? false,
                  });

                  setDetectedCountryCode(countryCode);
                  setEnteredCountryCode(countryCode);
                } catch (error) {
                  console.error(
                    "[WelcomeModal] Error adding postal code:",
                    error
                  );
                }
              } else {
                setDetectedCountryCode(firstItem?.country_code || "US");
                setEnteredCountryCode(firstItem?.country_code || "US");
              }
            } catch (error) {
              console.error("[WelcomeModal] Error getting IP data:", error);
              setDetectedCountryCode(firstItem?.country_code || "US");
              setEnteredCountryCode(firstItem?.country_code || "US");
            }
          } else {
            setPostalCode(firstItem.zipcode ?? "");
            setDetectedCountryCode(firstItem?.country_code || "US");
            setEnteredCountryCode(firstItem?.country_code || "US");
          }

          setShowModal(true);
        } else if (!showModal) {
          // Only check for phone modal if zip modal isn't showing
          // Check if we should show phone modal based on user's last active date and if phone exists
          const shouldShowPhone = await shouldShowPhoneModal(user);
          if (shouldShowPhone) {
            setShowPhoneModal(true);
          }
        }
      } catch (error) {
        console.error("[WelcomeModal] Error in fetchUser:", error);
        setDetectedCountryCode("US");
        setEnteredCountryCode("US");
        if (forceZipModal) {
          setShowModal(true);
          setShowPhoneModal(false); // Ensure phone modal doesn't show when forcing zip modal
        }
      }
    };

    if (
      isMounted &&
      (showWelcomeBanner || forceZipModal || forcePhoneModal) &&
      pathname &&
      !pathname.includes("checkout")
    ) {
      fetchUser();
    }
  }, [
    isMounted,
    pathname,
    showWelcomeBanner,
    supabase,
    shouldShowPhoneModal,
    forceZipModal,
    forcePhoneModal,
    hasInitialized,
    showModal, // Added to dependencies to track modal state
  ]);
  const handleZipCodeSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!postalCode || postalCode === "") {
      if (isUS) {
        setError("Please enter a ZIP Code");
      } else {
        setError("Please enter a Postal Code");
      }
      return;
    }

    if (!enteredCountryCode) {
      setError("Please select a Country");
      return;
    }

    setIsLoading(true);
    try {
      if (isValidPostalCode(postalCode)) {
        trackEvent(UserSignupEvents.USER_SUBMITTED_VALID_ZIPCODE);
        await addUserPostalCode({
          supabase,
          postalCode,
          countryCode: enteredCountryCode || detectedCountryCode,
          isFromIP: false,
          isEu: enteredCountryCode
            ? getIsEuCountry(enteredCountryCode)
            : getIsEuCountry(detectedCountryCode),
        });
        setShowModal(false);

        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;

        if (user) {
          const shouldShowPhone = await shouldShowPhoneModal(user);
          if (shouldShowPhone) {
            setShowPhoneModal(true);
          }
        }
      } else {
        if (isUS) {
          setError("Please provide a valid ZIP Code");
        } else {
          setError("Please provide a valid Postal Code");
        }
      }
    } catch (error) {
      console.error("Error submitting zipcode:", error);
      setError(
        "An error occurred while saving your postal code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseModal = useCallback(() => {
    trackEvent(UserSignupEvents.USER_DISREGARDED_ZIP_CODE_MODAL);
    setShowModal(false);
  }, []);

  // Don't render anything until component is mounted
  if (!isMounted) return null;

  // Don't show during onboarding - moved after all hooks
  if (pathname === "/suggested") {
    return null;
  }

  return (
    <>
      {showModal && (
        <Modal onClose={onCloseModal}>
          <div className="sm:mx-auto sm:w-full max-w-[80vw] sm:max-w-sm">
            <PunchUpLogo className="justify-center w-auto h-12 mx-auto md:h-16 fill-zinc-950" />
            <h2 className="mt-6 text-2xl font-bold leading-9 tracking-tight text-center text-gray-900">
              Welcome!
            </h2>
            <p className="mt-4 mb-2 text-left text-gray-900 text-md">
              {`Please provide your location so we can share local live comedy events with you. Without a ${isUS ? "ZIP Code" : "Postal Code"}, we'll still do our best to find nearby shows.`}
            </p>
          </div>
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <form
              className="w-full max-w-md space-y-4 border-black border-opacity-10"
              onSubmit={handleZipCodeSubmit}
              id="zipcode-form"
            >
              <PostalCodeFields
                postalCode={postalCode}
                onPostalCodeChange={(value) => setPostalCode(value)}
                defaultCountryCode={detectedCountryCode}
                onCountryCodeChange={(value) => setEnteredCountryCode(value)}
                isUS={isUS}
              />
              <button
                type="submit"
                className="w-full max-w-md px-4 py-3 font-semibold text-white rounded-md bg-primary focus:outline-none focus:shadow-outline"
              >
                {isLoading ? <Loader /> : "Let me know!"}
              </button>
              <div className="mt-2 text-red-500">{error}</div>
            </form>
          </div>
        </Modal>
      )}

      <AddPhoneModal isOpen={showPhoneModal} setIsOpen={setShowPhoneModal} />
    </>
  );
}
